import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Button,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";
import { tokens } from "src/locales/tokens";
import { useDispatch } from "src/store";
import FavoriteIcon from "@mui/icons-material/Favorite";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import type { Page as PageType } from "src/types/page";
import { thunks } from "src/thunks/analyze";
import { InstagramSearchForm } from "src/sections/instagram/instagram-search-form";
import type { InstagramVideoDataPost } from "src/types/analysis";
import { api } from "src/config";
const Page: PageType = () => {
  usePageView();

  const { t } = useTranslation();

  const [transcription, setTranscription] =
    useState<InstagramVideoDataPost | null>(null);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [hasGeneralError, setHasGeneralError] = useState(false);
  const [hasUrlNotFoundError, setHasUrlNotFoundError] = useState(false);
  const [isVideoTextCopied, setIsVideoTextCopied] = useState(false);
  const [isCaptionCopied, setIsCaptionCopied] = useState(false);

  const getImageUrl = useCallback((url: string) => {
    return `${api}/image/download?url=` + encodeURIComponent(url);
  }, []);

  const onSubmit = React.useCallback(
    async (url: string): Promise<void> => {
      setIsLoading(true);
      setHasUrlNotFoundError(false);
      setHasGeneralError(false);
      setTranscription(null);

      try {
        const data = await dispatch(thunks.getInstagramVideoTranscription(url));

        setTranscription(data);
      } catch (error) {
        if (error?.code === "Instagram.VideoNotFound") {
          setTranscription(null);
          setHasUrlNotFoundError(true);
        } else {
          setHasGeneralError(true);
        }
      }

      setIsLoading(false);
    },
    [dispatch]
  );

  const handleCopyVideoText = useCallback(() => {
    if (transcription?.formattedText) {
      // Remove HTML tags and markdown code block syntax
      const cleanText = transcription.formattedText
        .replace("```html", "")
        .replace("```", "")
        .replace(/<[^>]*>/g, "");

      navigator.clipboard
        .writeText(cleanText)
        .then(() => {
          setIsVideoTextCopied(true);
          setTimeout(() => {
            setIsVideoTextCopied(false);
          }, 2000); // Reset after 2 seconds
        })
        .catch((error) => console.error("Failed to copy text:", error));
    }
  }, [transcription]);

  const handleCopyCaption = useCallback(() => {
    if (transcription?.caption) {
      navigator.clipboard
        .writeText(transcription.caption)
        .then(() => {
          setIsCaptionCopied(true);
          setTimeout(() => {
            setIsCaptionCopied(false);
          }, 2000); // Reset after 2 seconds
        })
        .catch((error) => console.error("Failed to copy text:", error));
    }
  }, [transcription]);

  return (
    <>
      <Seo title={t(tokens.instagram.videoToText.title) as string} />
      <Box>
        <Box
          sx={{
            pt: {
              xs: "30px",
              md: "30px",
            },
            pb: {
              xs: "50px",
              md: "50px",
            },
          }}
        >
          <InstagramSearchForm
            value={""}
            hasGeneralError={hasGeneralError}
            hasUserNotFoundError={hasUrlNotFoundError}
            isLoading={isLoading}
            onSubmit={onSubmit}
            placeholderText={t(tokens.instagram.videoToText.urlPlaceholder)}
            buttonText={t(tokens.instagram.videoToText.transcribeButton)}
          />
        </Box>

        {isLoading && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Skeleton
                  variant="rounded"
                  height={30}
                />
                <Skeleton
                  variant="rounded"
                  height={30}
                />
                <Skeleton
                  variant="rounded"
                  height={30}
                />
                <Skeleton
                  variant="rounded"
                  height={30}
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        {transcription && !isLoading && (
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
              >
                {/* Image container */}
                <Box
                  sx={{
                    width: { xs: "100%", md: 200 },
                    minWidth: { md: 200 },
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    window.open(
                      transcription.inputUrl,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                >
                  <Box
                    component="img"
                    src={getImageUrl(transcription.displayUrl)}
                    alt="Instagram video thumbnail"
                    sx={{
                      width: "100%",
                      height: "auto",
                      borderRadius: 1,
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="flex-start"
                    sx={{ mt: 1 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <FavoriteIcon
                        color="action"
                        sx={{ fontSize: 18 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 14 }}
                      >
                        {transcription.likesCount?.toLocaleString() || "0"}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <VisibilityIcon
                        color="action"
                        sx={{ fontSize: 18 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 14 }}
                      >
                        {transcription.videoPlayCount?.toLocaleString() || "0"}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <ChatBubbleOutlineIcon
                        color="action"
                        sx={{ fontSize: 18 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 14 }}
                      >
                        {transcription.commentsCount?.toLocaleString() || "0"}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                {/* Content container */}
                <Stack
                  spacing={2}
                  flex={1}
                >
                  <Typography variant="h4">
                    {t(tokens.instagram.videoToText.text)}
                  </Typography>
                  <Typography
                    variant="body1"
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html: transcription.formattedText
                        .replace("```html", "")
                        .replace("```", ""),
                    }}
                    sx={{
                      "& p": { margin: 0 },
                      "& p + p": { marginTop: 2 },
                    }}
                  />
                  <Box>
                    <Button
                      onClick={handleCopyVideoText}
                      size="small"
                      variant="outlined"
                      startIcon={isVideoTextCopied ? null : <ContentCopyIcon />}
                      color={isVideoTextCopied ? "success" : "primary"}
                    >
                      {isVideoTextCopied
                        ? t(tokens.general.buttons.copied)
                        : t(tokens.general.buttons.copyToClipboard)}
                    </Button>
                  </Box>
                  {transcription?.caption && (
                    <>
                      <hr />
                      <Typography variant="h4">
                        {t(tokens.instagram.videoToText.caption)}
                      </Typography>

                      <Typography
                        variant="body1"
                        component="div"
                        dangerouslySetInnerHTML={{
                          __html: transcription?.caption
                            .replaceAll("\n", "<br />")
                            .replace("```html", "")
                            .replace("```", ""),
                        }}
                        sx={{
                          "& p": { margin: 0 },
                          "& p + p": { marginTop: 2 },
                        }}
                      />
                      <Box>
                        <Button
                          onClick={handleCopyCaption}
                          size="small"
                          variant="outlined"
                          startIcon={
                            isCaptionCopied ? null : <ContentCopyIcon />
                          }
                          color={isCaptionCopied ? "success" : "primary"}
                        >
                          {isCaptionCopied
                            ? t(tokens.general.buttons.copied)
                            : t(tokens.general.buttons.copyToClipboard)}
                        </Button>
                      </Box>
                    </>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {hasUrlNotFoundError && (
          <Card>
            <CardContent>
              <Typography color="error">
                {t(tokens.instagram.videoToText.videoNotFound)}
              </Typography>
            </CardContent>
          </Card>
        )}

        {hasGeneralError && (
          <Card>
            <CardContent>
              <Typography color="error">
                {t(tokens.instagram.videoToText.generalError)}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </>
  );
};

export default Page;
